import {Box, Grid} from '@material-ui/core'
import _ from 'lodash'
import {DndProvider, useDrag, useDrop} from 'react-dnd'
import {HTML5Backend} from 'react-dnd-html5-backend'
import generateEquipmentString from 'src/helpers/equipment'
import Action from 'src/models/Action'
import Caption from '../Typography/Caption'
import Subtitle1 from '../Typography/Subtitle1'
import {
  ActionCardContainer,
  ActionCardContent,
  ActionCardDetails,
  ActionCardHeader,
  ActionCardHeaderDragIcon,
  ActionCardThumbnail,
  ActionDropTargetIndicator,
} from './styled'

export type ActionsTableProps = {
  orderedActions: Action[]
  onActionsUpdated: (updatedActions: Action[]) => void
}

const DragTypeCard = 'Card'

type ActionCardProps = {
  action: Action
  index: number
  onActionReorder: (indexFrom: number, indexTo: number) => void
  style?: any
}
const ActionCard: React.FC<ActionCardProps> = ({
  action,
  index,
  onActionReorder,
  style,
}: ActionCardProps) => {
  const [{isDragging}, dragHandleRef, dragPreviewRef] = useDrag({
    type: DragTypeCard,
    item: {dragIndex: index},
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult<{droppedOnIndex: number}>()
      if (item && dropResult) {
        onActionReorder(item.dragIndex, dropResult.droppedOnIndex)
      }
    },
    collect: (monitor: any) => {
      return {
        isDragging: monitor.isDragging(),
      }
    },
  })

  const opacity = isDragging ? 0.4 : 1
  return (
    <ActionCardContainer ref={dragPreviewRef} style={{...style, opacity: opacity}}>
      <ActionCardHeader>
        <Grid ref={dragHandleRef}>
          <ActionCardHeaderDragIcon />
        </Grid>
      </ActionCardHeader>
      <ActionCardContent>
        <ActionCardThumbnail variant="square" src={action.thumbnailUrl} />
        <ActionCardDetails>
          <Subtitle1>{action.title}</Subtitle1>
          <Caption>{action.description}</Caption>
          <Caption>{generateEquipmentString(action.equipment, 'comma')}</Caption>
        </ActionCardDetails>
      </ActionCardContent>
    </ActionCardContainer>
  )
}

type ActionDropTargetProps = {
  style?: any
  index: number
}
const ActionDropTarget: React.FC<ActionDropTargetProps> = ({
  style,
  index,
}: ActionDropTargetProps) => {
  const [{isOver}, dropTargetRef] = useDrop({
    accept: DragTypeCard,
    drop: () => ({droppedOnIndex: index}),
    collect(monitor) {
      return {
        isOver: monitor.isOver(),
      }
    },
  })

  const background = isOver ? 'lightGreen' : 'transparent'

  return (
    <ActionDropTargetIndicator
      ref={dropTargetRef}
      style={{...style, backgroundColor: background}}
    />
  )
}

const ActionsTable: React.FC<ActionsTableProps> = ({
  orderedActions,
  onActionsUpdated,
}: ActionsTableProps) => {
  /// This is called when an action has been re-ordered.
  const onActionReorder = (indexFrom: number, indexTo: number) => {
    if (indexFrom < orderedActions.length && indexFrom !== indexTo) {
      // We need to copy the array to manipulate it.
      var updatedActions = [...orderedActions]

      // Grab the action we're moving.
      const movedAction = updatedActions[indexFrom]

      // Remove the moved action from the array.
      updatedActions.splice(indexFrom, 1)

      // Now add the action in the new spot.
      if (indexTo > updatedActions.length) {
        // Well this is easy, just add the action to the end!
        updatedActions.push(movedAction)
      } else {
        // Need to insert this in the middle of the array.
        if (indexTo > indexFrom) {
          // Since we're inserting ahead of the removed Action, the original indexTo is 1-too-many.
          updatedActions.splice(indexTo - 1, 0, movedAction)
        } else {
          // Since we're inserting behind the removed Action, the original indexTo is still ok.
          updatedActions.splice(indexTo, 0, movedAction)
        }
      }

      // Tell the caller.
      onActionsUpdated(updatedActions)
    }
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box display="flex" flexDirection="column">
        {_.map(orderedActions, (action: Action, index) => {
          return (
            <>
              <ActionDropTarget index={index} />
              <ActionCard action={action} index={index} onActionReorder={onActionReorder} />
            </>
          )
        })}
        <ActionDropTarget index={orderedActions.length} />
      </Box>
    </DndProvider>
  )
}

export default ActionsTable
